'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import {
  validateFiles,
  createFileWithUIState,
  cleanupPreviewUrl
} from '../utils/fileUtils'
import type {
  FileUploadConfig,
  FileWithUIState,
  ValidationResult,
  UploadResult,
  FileUploadHook,
  BatchUploadHook,
  FileValidationHook
} from '../types'

export interface UseFileUploadUIProps {
  config: FileUploadConfig
  uploadHook?: FileUploadHook
  batchUploadHook?: BatchUploadHook
  validationHook?: FileValidationHook
  onUpload?: (files: File[]) => Promise<UploadResult[]>
  onValidate?: (file: File) => ValidationResult
  initialFiles?: File[]

  // Event handlers
  onFilesAdded?: (files: File[]) => void
  onFileRemoved?: (file: File, index: number) => void
  onUploadStart?: (files: File[]) => void
  onUploadProgress?: (progress: any, file?: File) => void
  onUploadComplete?: (results: UploadResult[]) => void
  onUploadError?: (error: Error, file?: File) => void
  onValidationError?: (errors: ValidationResult[]) => void
}

export interface UseFileUploadUIReturn {
  // State
  files: FileWithUIState[]
  isUploading: boolean
  isDragging: boolean
  validationResults: ValidationResult[]
  uploadResults: UploadResult[]

  // Actions
  addFiles: (files: File[]) => void
  removeFile: (fileOrIndex: FileWithUIState | number) => void
  clearFiles: () => void
  uploadFiles: (files?: FileWithUIState[]) => Promise<void>
  retryUpload: (file: FileWithUIState) => void
  cancelUpload: () => void

  // Drag and drop
  handleDragOver: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void

  // File input
  triggerFileSelect: () => void
  fileInputRef: React.RefObject<HTMLInputElement>

  // Utilities
  getFileById: (id: string) => FileWithUIState | undefined
  getFilesByStatus: (status: FileWithUIState['status']) => FileWithUIState[]
  hasErrors: boolean
  hasWarnings: boolean
  canUpload: boolean
}

export function useFileUploadUI({
  config,
  uploadHook,
  batchUploadHook,
  validationHook,
  onUpload,
  onValidate,
  initialFiles = [],
  onFilesAdded,
  onFileRemoved,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  onValidationError
}: UseFileUploadUIProps): UseFileUploadUIReturn {

  // State
  const [files, setFiles] = useState<FileWithUIState[]>(() =>
    initialFiles.map(createFileWithUIState)
  )
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([])
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([])

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadAbortControllerRef = useRef<AbortController | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file.preview) {
          cleanupPreviewUrl(file.preview)
        }
      })

      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort()
      }
    }
  }, [files])

  // File validation
  const validateFilesInternal = useCallback((filesToValidate: File[]): ValidationResult[] => {
    if (validationHook) {
      return validationHook.validateFiles(filesToValidate)
    }

    if (onValidate) {
      return filesToValidate.map(onValidate)
    }

    return validateFiles(filesToValidate, config)
  }, [validationHook, onValidate, config])

  // Add files
  const addFiles = useCallback((newFiles: File[]) => {
    if (newFiles.length === 0) return

    // Validate files
    const validation = validateFilesInternal(newFiles)
    const validFiles = newFiles.filter((_, index) => validation[index].isValid)
    const invalidResults = validation.filter(result => !result.isValid)

    // Update validation results
    setValidationResults(prev => [...prev, ...invalidResults])

    if (invalidResults.length > 0) {
      onValidationError?.(invalidResults)
    }

    // Add valid files
    if (validFiles.length > 0) {
      const fileStates = validFiles.map(createFileWithUIState)

      setFiles(prev => {
        const combined = config.multiple ? [...prev, ...fileStates] : fileStates

        // Enforce max files limit
        if (config.maxFiles && combined.length > config.maxFiles) {
          return combined.slice(0, config.maxFiles)
        }

        return combined
      })

      onFilesAdded?.(validFiles)

      // Auto-upload if enabled
      if (config.autoUpload && (uploadHook || batchUploadHook || onUpload)) {
        uploadFiles(fileStates)
      }
    }
  }, [
    validateFilesInternal,
    config,
    onFilesAdded,
    onValidationError,
    uploadHook,
    batchUploadHook,
    onUpload
  ])

  // Remove file
  const removeFile = useCallback((fileOrIndex: FileWithUIState | number) => {
    const index = typeof fileOrIndex === 'number' ? fileOrIndex : files.findIndex(f => f.id === fileOrIndex.id)

    if (index === -1) return

    const file = files[index]

    // Cleanup preview URL
    if (file.preview) {
      cleanupPreviewUrl(file.preview)
    }

    setFiles(prev => prev.filter((_, i) => i !== index))
    onFileRemoved?.(file, index)
  }, [files, onFileRemoved])

  // Clear all files
  const clearFiles = useCallback(() => {
    files.forEach(file => {
      if (file.preview) {
        cleanupPreviewUrl(file.preview)
      }
    })

    setFiles([])
    setValidationResults([])
    setUploadResults([])

    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort()
    }
  }, [files])

  // Upload files
  const uploadFiles = useCallback(async (filesToUpload?: FileWithUIState[]) => {
    const targetFiles = filesToUpload || files.filter(f => f.status === 'pending')

    if (targetFiles.length === 0) return

    setIsUploading(true)
    onUploadStart?.(targetFiles)

    // Create abort controller
    uploadAbortControllerRef.current = new AbortController()

    try {
      // Update file statuses to uploading
      setFiles(prev => prev.map(file =>
        targetFiles.some(tf => tf.id === file.id)
          ? { ...file, status: 'uploading' as const, progress: 0 }
          : file
      ))

      let results: UploadResult[]

      if (batchUploadHook && targetFiles.length > 1) {
        // Use batch upload hook
        results = await batchUploadHook.uploadBatch(targetFiles, {
          onBatchProgress: (completed, total, partialResults) => {
            // Update progress for completed files
            setFiles(prev => prev.map(file => {
              const result = partialResults.find(r => r.file === file)
              if (result) {
                return {
                  ...file,
                  status: result.success ? 'completed' : 'error' as const,
                  progress: result.success ? 100 : 0,
                  error: result.success ? undefined : result.error,
                  result: result.success ? result : undefined
                }
              }
              return file
            }))

            onUploadProgress?.({
              percentage: (completed / total) * 100,
              loaded: completed,
              total: total
            })
          }
        })
      } else if (uploadHook) {
        // Use single upload hook for each file
        results = await Promise.all(
          targetFiles.map(async (file) => {
            try {
              const result = await uploadHook.upload(file, {
                onProgress: (progress: any) => {
                  setFiles(prev => prev.map(f =>
                    f.id === file.id
                      ? { ...f, progress: progress.percentage }
                      : f
                  ))
                  onUploadProgress?.(progress, file)
                }
              })

              // Update file status
              setFiles(prev => prev.map(f =>
                f.id === file.id
                  ? {
                      ...f,
                      status: result.success ? 'completed' : 'error' as const,
                      progress: result.success ? 100 : 0,
                      error: result.success ? undefined : result.error,
                      result: result.success ? result : undefined
                    }
                  : f
              ))

              return result
            } catch (error) {
              const errorResult: UploadResult = {
                success: false,
                file,
                error: error instanceof Error ? error.message : 'Upload failed'
              }

              // Update file status
              setFiles(prev => prev.map(f =>
                f.id === file.id
                  ? { ...f, status: 'error' as const, error: errorResult.error }
                  : f
              ))

              return errorResult
            }
          })
        )
      } else if (onUpload) {
        // Use direct upload handler
        results = await onUpload(targetFiles)

        // Update file statuses based on results
        setFiles(prev => prev.map(file => {
          const result = results.find(r => r.file === file)
          if (result) {
            return {
              ...file,
              status: result.success ? 'completed' : 'error' as const,
              progress: result.success ? 100 : 0,
              error: result.success ? undefined : result.error,
              result: result.success ? result : undefined
            }
          }
          return file
        }))
      } else {
        // No upload handler available
        results = targetFiles.map(file => ({
          success: false,
          file,
          error: 'No upload handler configured'
        }))
      }

      setUploadResults(prev => [...prev, ...results])

      const successfulUploads = results.filter(r => r.success)
      const failedUploads = results.filter(r => !r.success)

      if (successfulUploads.length > 0) {
        onUploadComplete?.(successfulUploads)
      }

      if (failedUploads.length > 0) {
        const error = new Error(`${failedUploads.length} uploads failed`)
        onUploadError?.(error)
      }

    } catch (error) {
      console.error('Upload error:', error)

      // Mark all files as error
      setFiles(prev => prev.map(file =>
        targetFiles.some(tf => tf.id === file.id)
          ? { ...file, status: 'error' as const, error: 'Upload failed' }
          : file
      ))

      onUploadError?.(error instanceof Error ? error : new Error('Upload failed'))
    } finally {
      setIsUploading(false)
      uploadAbortControllerRef.current = null
    }
  }, [
    files,
    uploadHook,
    batchUploadHook,
    onUpload,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onUploadError
  ])

  // Retry upload
  const retryUpload = useCallback((file: FileWithUIState) => {
    // Reset file status
    setFiles(prev => prev.map(f =>
      f.id === file.id
        ? { ...f, status: 'pending' as const, error: undefined, progress: 0 }
        : f
    ))

    // Trigger upload for this file
    if (config.autoUpload) {
      uploadFiles([file])
    }
  }, [config.autoUpload, uploadFiles])

  // Cancel upload
  const cancelUpload = useCallback(() => {
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort()
    }

    setIsUploading(false)

    // Reset uploading files to pending
    setFiles(prev => prev.map(file =>
      file.status === 'uploading'
        ? { ...file, status: 'pending' as const, progress: 0 }
        : file
    ))
  }, [])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    if (!config.dragDropEnabled) return

    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles)
    }
  }, [config.dragDropEnabled, addFiles])

  // File input handler
  const triggerFileSelect = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Utility functions
  const getFileById = useCallback((id: string) => {
    return files.find(f => f.id === id)
  }, [files])

  const getFilesByStatus = useCallback((status: FileWithUIState['status']) => {
    return files.filter(f => f.status === status)
  }, [files])

  // Computed values
  const hasErrors = validationResults.some(r => !r.isValid)
  const hasWarnings = validationResults.some(r => r.warnings.length > 0)
  const canUpload = files.some(f => f.status === 'pending') && !isUploading

  return {
    // State
    files,
    isUploading,
    isDragging,
    validationResults,
    uploadResults,

    // Actions
    addFiles,
    removeFile,
    clearFiles,
    uploadFiles,
    retryUpload,
    cancelUpload,

    // Drag and drop
    handleDragOver,
    handleDragLeave,
    handleDrop,

    // File input
    triggerFileSelect,
    fileInputRef,

    // Utilities
    getFileById,
    getFilesByStatus,
    hasErrors,
    hasWarnings,
    canUpload
  }
}